import React, { useEffect, useState } from 'react';
import { usePermissions, useRecordContext, useTranslate } from 'react-admin';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { NotificationsActive } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient, type ApiError } from '../../utils/apiClient';
import { hasPermission } from '../../utils/hasPermission';
import { AlertStateChip, SeverityChip } from './AlertChips';
import { kioskAlertsLink, when } from './alertText';
import type { KioskAlert } from './types';

export const RECENT_ALERTS_LIMIT = 10;

export const KioskRecentAlertsPanel = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const [rows, setRows] = useState<KioskAlert[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const kioskId = record ? String(record.id) : null;
  const allowed = hasPermission(permissions as string[] | null | undefined, 'kiosk-alerts:read:all');

  useEffect(() => {
    if (kioskId === null || !allowed) return undefined;
    let active = true;
    apiClient
      .get<{ data: KioskAlert[]; total: number }>(`/api/backend/kiosk-alerts?kioskId=${encodeURIComponent(kioskId)}&status=all&sort=raisedAt&limit=${RECENT_ALERTS_LIMIT}&offset=0`)
      .then((page) => {
        if (!active) return;
        setRows(page.data);
        setError(null);
      })
      .catch((failure) => {
        if (active) setError((failure as ApiError).message);
      });
    return () => {
      active = false;
    };
  }, [kioskId, allowed]);

  if (kioskId === null || !allowed) return null;
  const t = (key: string, options?: Record<string, unknown>) => translate(`resources.kiosk-alerts.recent.${key}`, options);

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <NotificationsActive sx={{ color: '#00C853' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' }, flex: 1 }}>{t('title')}</Typography>
          <Button size="small" onClick={() => navigate(kioskAlertsLink({ kioskId, status: 'all' }))}>{t('viewAll')}</Button>
        </Box>
        {error !== null && (
          <Typography variant="body2" color="error" data-testid="recent-alerts-error" sx={{ mb: 1 }}>{t('loadFailed', { error })}</Typography>
        )}
        {rows !== null && rows.length === 0 && <Typography variant="body2" color="text.secondary">{t('empty')}</Typography>}
        {rows !== null && rows.map((row) => (
          <Box
            key={row.id}
            data-testid={`recent-alert-${row.id}`}
            onClick={() => navigate(`/kiosk-alerts/${row.id}/show`)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 1, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}
          >
            <SeverityChip severity={row.severity} />
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{row.label}</Typography>
            <AlertStateChip status={row.status} />
            <Typography variant="caption" color="text.secondary">{when(row.raisedAt)}</Typography>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
};
